/**
 * The performance view's layout: one line, sliding left, never stopping.
 *
 * A `Caption` already knows how to build the line — per-glyph axes, gestures
 * placed at the moment they happened, contested words faded. What it does not
 * have is a shape to live in. In the lab it sits in a wrapping box, which is
 * right for reading a take back and wrong for watching one happen: the line
 * reflows every time a word lands, so the thing you are trying to watch keeps
 * jumping to a new position.
 *
 * A ticker fixes the reading position instead of the text. The newest glyph is
 * always at the same place on screen and everything else slides away from it,
 * which is the only arrangement where a `wght` swelling on a vowel is visible as
 * a swelling rather than as part of a general reshuffle.
 *
 * ### Anchored to the newest glyph, not run at a fixed speed
 *
 * The obvious ticker moves at a constant rate. It cannot work here, because the
 * text arrives at the rate somebody is talking: pick a speed and either a fast
 * sentence piles up off the right edge or a pause runs the line dry. So the
 * offset is derived — always exactly enough to put the end of the track at the
 * anchor — and then *eased* toward, so the motion reads as gliding rather than
 * as a series of jumps.
 *
 * ### Nothing is removed for being old
 *
 * Off-screen elements stay in the DOM until there are enough of them to matter,
 * because the merge inserts in spoken time: a word finalised late belongs behind
 * one already placed, and a gesture released at LAG belongs behind both. Pruning
 * aggressively would mean an insertion could target something that had been
 * thrown away. The cap is generous and measured in elements, not seconds.
 */

const MAX_ELEMENTS = 400;

export class Ticker {
  /**
   * @param {object} o
   * @param {HTMLElement} o.viewport  clipping box, position: relative
   * @param {HTMLElement} o.track     the moving element, inside it
   * @param {import('./caption.js').Caption} o.caption  renders into `track`
   * @param {number} [o.anchor] where the newest glyph sits, as a fraction of the
   *   viewport width. Not 1: text arriving exactly at the edge is unreadable
   *   until it has moved, and interim words need somewhere to be.
   * @param {number} [o.ease] 0..1 per frame, toward the target offset.
   */
  constructor({ viewport, track, caption, anchor = 0.82, ease = 0.12 }) {
    this.viewport = viewport;
    this.track = track;
    this.caption = caption;
    this.anchor = anchor;
    this.ease = ease;

    /** Where the track is drawn, in px of leftward travel. */
    this.offset = 0;
    /** Where it wants to be. */
    this.target = 0;
  }

  /**
   * Advance one frame. Cheap: two layout reads and one transform write.
   *
   * `scrollWidth` is a forced reflow, which is exactly the kind of thing this
   * project avoids in a loop — but it is read once per frame against a line
   * whose contents changed at most a few times per second, and the alternative
   * is tracking the width of every element by hand and being wrong about
   * kerning.
   */
  /**
   * Advance one frame.
   *
   * @param {?{x: number, y: number}} [at] where the newest glyph should sit, in
   *   viewport pixels. Given, the line is pinned there — which is how the face
   *   view has the text come out of the mouth and follow it around. Omitted, it
   *   falls back to a fixed column and the line behaves as a plain ticker.
   */
  frame(at = null) {
    const width = this.viewport.clientWidth;
    if (!width) return;

    const end = this.track.scrollWidth;
    const anchorX = at ? at.x : width * this.anchor;

    // Clamped at zero only for the fixed anchor, where it is what holds the
    // first few words still instead of flying them in from the right. Pinned to
    // a mouth the offset must be free to go negative, or a short line would
    // start at the left edge of the screen rather than at the speaker's lips.
    const raw = end - anchorX;
    this.target = at ? raw : Math.max(0, raw);

    const delta = this.target - this.offset;
    // Snap when close, so the transform stops changing at rest and the
    // compositor has nothing to do between utterances.
    this.offset = Math.abs(delta) < 0.5 ? this.target : this.offset + delta * this.ease;

    // Vertically centred on the anchor rather than sitting on it: the text is
    // level with the mouth, which is what "coming out of it" looks like.
    const y = at ? at.y - this.track.offsetHeight / 2 : 0;
    this.track.style.transform = `translate(${-this.offset}px, ${y}px)`;

    this._prune();
  }

  /**
   * Drop the oldest elements once there are far more than can be seen.
   *
   * By element count rather than by position: an element that has slid off the
   * left is still a valid insertion target for a word ASR is about to finalise,
   * and removing it too eagerly would put that word at the wrong end of the
   * line.
   *
   * The caption's `_committedRendered` is deliberately left alone. It is a
   * high-water mark of how far into `transcript.committed` the renderer has got
   * — not a count of what is on screen — so decrementing it would make the next
   * `render()` re-add words that were pruned precisely because they were no
   * longer wanted, and the line would grow a duplicate of its own past every
   * time it was trimmed.
   *
   * Removed elements simply never come back, which is the intent.
   */
  _prune() {
    const committed = this.caption.committedEl;
    let excess = committed.childElementCount - MAX_ELEMENTS;
    while (excess-- > 0 && committed.firstElementChild) {
      const el = committed.firstElementChild;
      // Measured before it leaves. The track is about to get shorter, so the
      // offset has to shrink by the same amount in the same frame — otherwise
      // the target drops, the easing chases it, and a prune reads as the whole
      // line sliding backwards for half a second.
      const width = el.getBoundingClientRect().width;
      el.remove();
      this.offset = Math.max(0, this.offset - width);
    }
  }

  /** Back to the start, with the line empty. */
  reset() {
    this.offset = 0;
    this.target = 0;
    this.track.style.transform = 'translateX(0px)';
  }
}

export default Ticker;
